import { message } from 'antd'
import { produce } from 'immer'
import model, { ModelWithState } from '@/utils/model'
import { DEFAULT_OPTIONS } from './components/TemplateModal'
import { GroupMapType, GroupType, formatGroups } from './components/InputComponets/RuleCascader'
import { SnickType, SnicksMapType, formatSnicks } from './components/InputComponets/SnickCascader'
import { cascaderValueMap, flatValueMap, formatRuleList } from './utils'
import { fetchShops } from '../../services/common'
import {
  addNotifyRule,
  deleteNotifyRule,
  getNotifyRobot,
  getNotifyRule,
  getQcGroups,
  getSnickTree,
  updateNotifyRule,
} from './services'
import { DvaState } from '@/types/react-redux'
import { isNewLayout } from '@/utils/utils'

export type RuleType = {
  id?: string
  app_robot_id?: string
  remark: string
  platform: string
  showPlatform?: boolean
  seller_nick: string
  seller_nicks: string[]
  departments: string[]
  qc_rule_names: string[]
  qc_rules: string[]
  notify_style: string[]
  alert_level: string[]
  notify_timer: boolean
  duration_time: number
  robot_name?: string
  status: 1 | 2 // 1代表正式提交，2代表暂存草稿
  isChanging?: boolean // 前端记录是否正在发送修改请求，用于展示loading状态
  dataIndex: number // 同一种平台的第dataIndex个
  tableIndex: number // 第几个平台
  isLast: boolean // 是否是一类平台中的最后一个
}

type OptionMapType = Record<string, { label: string; value: string }[]>

export interface ModelState {
  tableList: RuleType[][]
  currentTableIndex: number
  currentDataIndex: number
  shopsMap: OptionMapType
  groups: GroupType[][] // 存储各个等级的质检项，[所有质检项，初级，中级，高级]
  allGroupsMap: GroupMapType
  snicks: SnickType[]
  snicksMap: SnicksMapType
  robotOptions: any[]
  templateVisible: boolean
  errorRowInfo: {
    tableIndex: number
    dataIndex: number
  }
}

export const EMPTY_RULE: RuleType = {
  app_robot_id: undefined,
  remark: '',
  platform: '',
  seller_nick: '',
  seller_nicks: [],
  departments: [],
  qc_rule_names: [],
  qc_rules: [],
  notify_style: [...DEFAULT_OPTIONS],
  alert_level: [],
  notify_timer: false,
  duration_time: 0,
  status: 2,
}

export const NO_ALARM_PLATFORM = 'jdqc'
export const NULL_ROBOT = '000000000000000000000000'

const getEmptyGroups = (): GroupType[][] => Array.from({ length: 4 }, () => [])

const UserModel: ModelWithState<ModelState> = model.extend({
  namespace: 'alarmRules',

  state: {
    tableList: [],
    robotOptions: [],
    templateVisible: false,
    currentTableIndex: -1,
    currentDataIndex: -1,
    shopsMap: {},
    groups: getEmptyGroups(),
    allGroupsMap: {
      valueMap: new Map(),
      flatMap: new Map(),
    },
    snicks: [],
    snicksMap: {
      valueMap: new Map(),
      flatMap: new Map(),
    },
    errorRowInfo: {
      tableIndex: -1,
      dataIndex: -1,
    },
  },

  subscriptions: {
    setup: ({ listen, dispatch }) => {
      listen(isNewLayout ?'/qcManage/alarm' :`/alarm`, () => {
        dispatch({ type: 'fetchNotifyRuleList' })
        dispatch({ type: 'fetchSnicks' })
        dispatch({ type: 'fetchShops' })
        dispatch({ type: 'fetchRobotOptions' })
        dispatch({ type: 'fetchGroups' })
      })
    },
  },

  reducers: {
    setState: produce((state, action) => ({
      ...state,
      ...action.payload,
    })),
    addEmptyRule: produce((state, action) => {
      const { tableIndex } = action.payload
      state.tableList[tableIndex].push({
        ...EMPTY_RULE,
        id: `temp_${Date.now()}`,
        platform: state.tableList[tableIndex][0].platform,
      })
    }),
    copyRule: produce((state, action) => {
      const { tableIndex, dataIndex } = action.payload
      const copyData = state.tableList[tableIndex][dataIndex]

      state.tableList[tableIndex].splice(dataIndex + 1, 0, {
        ...EMPTY_RULE,
        platform: copyData.platform,
        seller_nicks: [...(copyData.seller_nicks || [])],
        departments: [...(copyData.departments || [])],
        qc_rule_names: [...(copyData.qc_rule_names || [])],
        qc_rules: [...(copyData.qc_rules || [])],
      })
    }),
    changeRule: produce((state, action) => {
      const { tableIndex, dataIndex, key, value } = action.payload
      const currentData = state.tableList[tableIndex][dataIndex]

      if (key === 'qc_rules') {
        currentData[key] = value.ruleIds
        currentData.qc_rule_names = value.ruleNames
      } else {
        currentData[key] = value
      }
    }),
    deleteTempRule: produce((state, action) => {
      const { tableIndex, dataIndex } = action.payload
      const currentTable = state.tableList[tableIndex]

      if (currentTable.length === 1) {
        return
      }

      state.tableList[tableIndex].splice(dataIndex, 1)
    }),
  },

  effects: {
    *fetchNotifyRuleList(_, { call, put, select }) {
      const response = yield call(getNotifyRule)
      const platformsData: string[] = yield select(
        (store: DvaState) => store.user.company_info.platforms
      )
      const tableList = formatRuleList(
        response?.data?.list || [],
        platformsData.filter((v) => v !== NO_ALARM_PLATFORM)
      )

      yield put({
        type: 'setState',
        payload: {
          tableList,
        },
      })
    },
    *fetchShops({ payload }, { call, put }) {
      const res = yield call(fetchShops, payload)
      const shopsMap: OptionMapType = {}
      res?.data?.shops?.forEach((v) => {
        shopsMap[v.platform] = [
          ...(shopsMap[v.platform] || []),
          {
            label: v.plat_shop_name || v.seller_nick,
            value: v.seller_nick,
          },
        ]
      })
      yield put({
        type: 'setState',
        payload: {
          shopsMap,
        },
      })
    },
    *deleteRule({ payload }, { call, put, select }) {
      const { tableIndex, dataIndex } = payload
      const tableList: RuleType[][] = yield select((store: DvaState) => store.alarmRules.tableList)

      const { id } = tableList[tableIndex][dataIndex]

      // 没有id，只需要删除本地的list
      if (!id || id.startsWith('temp_')) {
        yield put({
          type: 'deleteTempRule',
          payload: {
            tableIndex,
            dataIndex,
          },
        })
        return
      }

      const res = yield call(deleteNotifyRule, { id })
      if (res && !res.code) {
        yield put({ type: 'fetchNotifyRuleList' })
        message.success('删除成功')
        return
      }
      message.error('删除失败')
    },
    *updateOrAddRule({ payload }, { call, put }) {
      let result
      const isUpdate = Boolean(payload.id)
      const showMessage = payload.status === 1 // 更新草稿不需要显示提示消息

      if (isUpdate) {
        result = yield call(updateNotifyRule, payload)
      } else {
        result = yield call(addNotifyRule, payload)
      }

      yield put({
        type: 'changeRule',
        payload: {
          ...payload,
          key: 'isChanging',
          value: false,
        },
      })
      if (result && !result.code) {
        yield put({ type: 'fetchNotifyRuleList' })

        if (showMessage) {
          message.success(`配置${isUpdate ? '修改' : ''}成功`)
        }

        return
      }

      if (showMessage) {
        message.error(`配置${isUpdate ? '修改' : ''}失败`)
      }
    },
    *submitRule({ payload }, { put, select }) {
      const { tableIndex, dataIndex, isSubmitDraft } = payload
      const tableList: RuleType[][] = yield select((store: DvaState) => store.alarmRules.tableList)

      const updatePayload = tableList[tableIndex][dataIndex]

      const { app_robot_id, status } = updatePayload

      // 正式提交才会触发校验，草稿的修改不需要
      if (!app_robot_id && (status === 1 || isSubmitDraft)) {
        message.error('告警群设置不能为空，请选择一个告警群进行设置！')
        yield put({
          type: 'setState',
          payload: {
            errorRowInfo: {
              tableIndex,
              dataIndex,
            },
          },
        })
        return
      }

      // 标记正在修改
      yield put({
        type: 'changeRule',
        payload: {
          ...payload,
          key: 'isChanging',
          value: true,
        },
      })

      yield put({
        type: 'updateOrAddRule',
        // 提交草稿时需要将status改为1
        payload: {
          ...updatePayload,
          status: isSubmitDraft ? 1 : updatePayload.status,
          // 如果是temp开头的id，需要删除 说明是草稿的临时id
          id: updatePayload.id?.startsWith('temp_') ? undefined : updatePayload.id,
        },
      })
    },
    *onChangeRule({ payload }, { put, select }) {
      // 失焦修改
      const { tableIndex, dataIndex, key, value } = payload
      const tableList: RuleType[][] = yield select((store: DvaState) => store.alarmRules.tableList)

      const currentData = tableList[tableIndex][dataIndex]
      const currentVal = JSON.stringify(currentData[key])

      // 判断当前值是否改变
      // qc_rule传入的值需要判断ruleIds
      if (key === 'qc_rules' && currentVal === JSON.stringify(value.ruleIds)) return

      // 普通值
      if (currentVal === JSON.stringify(value)) return

      yield put({
        type: 'changeRule',
        payload: {
          ...payload,
        },
      })

      // 正式提交后，修改不会自动保存，需要手动点击提交按钮
      if (currentData.status === 1) return

      yield put({
        type: 'submitRule',
        payload: {
          ...payload,
        },
      })
    },
    *onCopyRule({ payload }, { put }) {
      yield put({
        type: 'copyRule',
        payload: {
          ...payload,
        },
      })
      yield put({
        type: 'submitRule',
        payload: {
          tableIndex: payload.tableIndex,
          dataIndex: payload.dataIndex + 1,
        },
      })
    },
    *sumbitTemplate({ payload }, { put, select }) {
      const { value } = payload
      const { currentTableIndex, currentDataIndex }: DvaState['alarmRules'] = yield select(
        (store: DvaState) => store.alarmRules
      )

      yield put({
        type: 'onChangeRule',
        payload: {
          tableIndex: currentTableIndex,
          dataIndex: currentDataIndex,
          key: 'notify_style',
          value,
        },
      })
    },
    *fetchSnicks(_, { call, put }) {
      const response = yield call(getSnickTree, {
        show_not_allocated: true,
      })
      let snicks: SnickType[] = []
      if (response && !response.code) {
        snicks = formatSnicks(response?.data?.deps?.subs || [])
      }
      const snicksMap: SnicksMapType = {
        valueMap: cascaderValueMap(snicks),
        flatMap: flatValueMap(snicks),
      }

      yield put({
        type: 'setState',
        payload: {
          snicks,
          snicksMap,
        },
      })
    },
    *fetchGroups(_, { call, put }) {
      const groups = getEmptyGroups()

      for (let i = 0; i < 4; i++) {
        const response = yield call(getQcGroups, i !== 0 ? { alert_level: i } : undefined)
        if (response && !response.code) {
          groups[i] = formatGroups(response?.data?.list || [])
        }
      }

      const maps = {
        valueMap: cascaderValueMap(groups[0]),
        flatMap: flatValueMap(groups[0]),
      }

      yield put({
        type: 'setState',
        payload: {
          groups,
          allGroupsMap: maps,
        },
      })
    },
    *fetchRobotOptions({ payload }, { call, put }) {
      const response = yield call(getNotifyRobot, payload)
      yield put({
        type: 'setState',
        payload: {
          robotOptions: response?.data?.robots || [],
        },
      })
    },
  },
})

export default UserModel
